import styles from "./InTheProcess.module.css";
import processSteps from "../../processSteps";
import { useState, useMemo, memo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button, Selector } from "@wwg/ui";
import { IconUndo } from "@wwg/icons";
import { useFreshState } from "shared/hooks/useFreshState";
import { IconButton } from "shared/components/IconButton/IconButton";
import { SolidSelect } from "shared/components/SolidSelect/SolidSelect";
import { contactPropType } from "shared/propTypes";
import { GivenSection } from "./GivenSection/GivenSection";
import { useTools } from "./useTools";
import { useSteps } from "./useSteps";
import environment from "environment";

export const InTheProcess = memo(({ contact }) => {
  const { t } = useTranslation();
  const tools = useTools({ contact });
  const steps = useSteps({ contact });

  const audioSelectRef = useRef();
  const bookSelectRef = useRef();

  const [audioIsOpen, setAudioIsOpen] = useState(false);
  const [bookIsOpen, setBookIsOpen] = useState(false);

  const shouldShowGivenBook = environment.shouldShowGivenBook 

  const progression = useMemo(() => {
    return contact.progression.map((x) => ({
      ...x,
      ...processSteps.find((s) => s.name === x.name),
    }));
  }, [contact.progression]);

  const stepNumbers = useMemo(() => {
    const byName = {};

    for (const step of progression) {
      byName[step.name] = (byName[step.name] || 0) + 1;
    }

    return byName;
  }, [progression]);

  // Calculate step list from current progression.
  const stepList = useMemo(() => {
    const list = [];

    for (const step of processSteps) {
      if (step.name === "Prospect" && progression.length > 0) {
        continue;
      }

      const num = stepNumbers[step.name];

      list.push(
        <option value={step.name} key={step.name + num}>
          {num > 1
            ? t(step.labelKey + "_number", { number: num })
            : t(step.labelKey)}
        </option>
      );

      if (progression[0] && step.name === progression[0].name) {
        const num = stepNumbers[step.name] + 1;

        list.push(
          <option value={step.name} key={step.name + num}>
            {num > 1
              ? t(step.labelKey + "_number", { number: num })
              : t(step.labelKey)}
          </option>
        );
      }
    }

    return list;
  }, [progression, stepNumbers]);

  const [selectedStep, setSelectedStep] = useFreshState(
    progression[0] || processSteps[0]
  );

  const [canUndoStep, setCanUndoStep] = useState(false);

  return (
    <div
      className={styles.container}
      style={{
        backgroundColor: selectedStep.color,
        color: selectedStep.textColor,
        "--color-text": selectedStep.textColor,
        "--color-input-border": selectedStep.textColor,
      }}
    >
      <section className={styles.inTheProcess}>
        <header className={styles.processHeader}>
          <span>{t("labels.prospectInTheProcess")}</span>

          {canUndoStep && (
            <IconButton
              icon={<IconUndo size={20} />}
              disabled={
                progression.length === 0 || steps.undoStepMutation.isLoading
              }
              onClick={() => {
                steps.undoStepMutation.mutate(progression[0].stepId);
                setSelectedStep(
                  progression[1] ? progression[1] : progression[0]
                );
                setCanUndoStep(false);
              }}
            >
              {t("buttons.undo")}
            </IconButton>
          )}
        </header>

        <div className={styles.selectorRow}>
          <SolidSelect
            value={selectedStep.name}
            onChange={(e) => {
              const name = e.target.value;
              const newStep = steps.steps.find((s) => s.name === name);
              setSelectedStep(newStep);
              setCanUndoStep(true);
              steps.addStepMutation.mutate(newStep.id);
            }}
          >
            {stepList}
          </SolidSelect>
        </div>
      </section>

      <section className={styles.bsmUsed}>
        <header>
          <span className={styles.bsmHeader}>
            {t("labels.prospectBSMsUsed")}
          </span>
        </header>

        <div className={styles.givenBSMs}>
          <GivenSection
            title={t("labels.prospectGivenAudio")}
            isOpen={audioIsOpen}
          >
            <div className={styles.selectorRow}>
              <SolidSelect ref={audioSelectRef}>
                {tools.audio.map((tool) => (
                  <option key={tool.id} value={tool.id}>
                    {tool.name}
                  </option>
                ))}
              </SolidSelect>
              <Button
                onClick={() => {
                  const id = Number(audioSelectRef.current.value);

                  tools.addToolMutation.mutate(id);
                  setAudioIsOpen(false);
                }}
              >
                {t("buttons.add")}
              </Button>
            </div>
          </GivenSection>

          {tools.currentStepAudio && tools.currentStepAudio.length > 0 && (
            <ul className={styles.toolList}>
              {tools.currentStepAudio.map((audio) => (
                <li key={audio.logIds[0]}>
                  <div className={styles.toolItem}>
                    <span>{audio.name}</span>
                    <IconButton
                      icon={<IconUndo size={20} />}
                      onClick={(e) => {
                        tools.deleteToolMutation.mutate({
                          handoutId: audio.handoutId,
                          undo: true,
                        });
                      }}
                    >
                      {t("buttons.undo")}
                    </IconButton>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <hr className={styles.divider} />

          <GivenSection
            title={t("labels.prospectGivenBOV")}
            onAdd={() => {
              tools.addToolMutation.mutate(tools.bov[0].id);
            }}
          />

          {tools.currentStepBOV && tools.currentStepBOV.length > 0 && (
            <ul className={styles.toolList}>
              {tools.currentStepBOV.map((bov, i) => (
                <li key={i}>
                  <div className={styles.toolItem}>
                    <span>BOV</span>
                    <IconButton
                      icon={<IconUndo size={20} />}
                      onClick={(e) => {
                        tools.deleteToolMutation.mutate({
                          handoutId: bov.handoutId,
                          undo: true,
                        });
                      }}
                    >
                      {t("buttons.undo")}
                    </IconButton>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <hr className={styles.divider} />

          {(shouldShowGivenBook && <GivenSection
            title={t("labels.prospectGivenBook")}
            isOpen={bookIsOpen}
          >
            <div className={styles.selectorRow}>
              <SolidSelect ref={bookSelectRef}>
                {tools.book.map((tool) => (
                  <option key={tool.id} value={tool.id}>
                    {tool.name}
                  </option>
                ))}
              </SolidSelect>
              <Button
                onClick={() => {
                  const id = Number(bookSelectRef.current.value);

                  tools.addToolMutation.mutate(id);
                  setBookIsOpen(false);
                }}
              >
                {t("buttons.add")}
              </Button>
            </div>
          </GivenSection>)}

          {tools.currentStepBooks && tools.currentStepBooks.length > 0 && (
            <ul className={styles.toolList}>
              {tools.currentStepBooks.map((book) => (
                <li key={book.logIds[0]}>
                  <div className={styles.toolItem}>
                    <span>{book.name}</span>
                    <IconButton
                      icon={<IconUndo size={20} />}
                      onClick={(e) => {
                        tools.deleteToolMutation.mutate({
                          handoutId: book.handoutId,
                          undo: true,
                        });
                      }}
                    >
                      {t("buttons.undo")}
                    </IconButton>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
});

InTheProcess.propTypes = {
  contact: contactPropType.isRequired,
};
